/**
 * ConversationDialog — full back-and-forth thread with one phone number.
 *
 * Inbound messages are aligned left (grey), outbound aligned right (dark).
 * The composer at the bottom sends a new outbound SMS to the same number,
 * with template support, then refreshes the thread. Reuses the same
 * merge-field convention as SmsQuickSendDialog for consistency.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Send, RefreshCw, AlertTriangle, CheckCircle2, ArrowDown, ArrowUp,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

const MERGE_HINTS = ["{first_name}", "{name}"];

function applyMerge(body, fields) {
  return (body || "").replace(/\{([a-zA-Z0-9_]+)\}/g, (_, k) =>
    fields?.[k] == null ? "" : String(fields[k]),
  );
}

const INTENT_STYLE = {
  confirm:      { label: "confirmed",  cls: "border-emerald-300 text-emerald-700" },
  cancel:       { label: "cancel",     cls: "border-amber-300 text-amber-700" },
  reschedule:   { label: "reschedule", cls: "border-blue-300 text-blue-700" },
  opt_out:      { label: "opt-out",    cls: "border-red-300 text-red-700" },
  opt_back_in:  { label: "opt-in",     cls: "border-emerald-300 text-emerald-700" },
  unknown:      { label: "reply",      cls: "border-zinc-300 text-zinc-700" },
  ignored:      { label: "ignored",    cls: "border-zinc-300 text-zinc-500" },
};

export default function ConversationDialog({
  open,
  onClose,
  phone,
  recipientName,          // optional — used for merge fields
}) {
  const [items, setItems] = useState([]);
  const [outCount, setOutCount] = useState(0);
  const [inCount, setInCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const firstName = useMemo(() => (recipientName || "").split(" ")[0] || "", [recipientName]);

  async function load() {
    if (!phone) return;
    setLoading(true);
    try {
      const r = await api.get(`/sms/conversation/${encodeURIComponent(phone)}`);
      setItems(r.data?.items || []);
      setOutCount(r.data?.outbound_count || 0);
      setInCount(r.data?.inbound_count || 0);
    } catch (e) {
      toast.error("Could not load conversation", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setBody("");
    setSelectedTpl("");
    load();
    api.get("/sms/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone]);

  // Auto-scroll to the newest message when items change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [items.length]);

  function applyTemplate(tid) {
    setSelectedTpl(tid);
    const t = templates.find((x) => x.id === tid);
    if (t) setBody(t.body);
  }

  async function send() {
    if (!body.trim()) {
      toast.error("Message body is empty");
      return;
    }
    setSending(true);
    try {
      const rendered = applyMerge(body, {
        first_name: firstName,
        name: recipientName || "",
      });
      const r = await api.post("/sms/send", {
        messages: [{
          to: phone,
          body: rendered,
          recipient_name: recipientName,
        }],
        source: "conversation_reply",
      });
      const d = r.data || {};
      if ((d.sent_count ?? 0) > 0) {
        toast.success("Reply sent",
          { description: d.total_price != null ? `Cost: $${d.total_price.toFixed(2)}` : undefined });
        setBody("");
        setSelectedTpl("");
        await load();
      } else if ((d.skipped_opt_out_count ?? 0) > 0) {
        toast.warning("Message skipped — recipient has opted out.");
      } else {
        const err = (d.results || [])[0]?.error_text;
        toast.error("Send failed", { description: err || "See history for details" });
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="conversation-dialog">
        <DialogHeader className="p-5 border-b border-zinc-200">
          <DialogTitle className="flex items-center gap-3">
            <span>Conversation with {recipientName || phone}</span>
            <span className="text-xs font-mono-num text-zinc-500">{phone}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="ml-auto h-7 px-2 border-zinc-300"
              data-testid="conversation-refresh"
            >
              <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
            </Button>
          </DialogTitle>
          <div className="text-2xs text-zinc-500 mt-1 flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <ArrowUp className="h-3 w-3" />{outCount} sent
            </span>
            <span className="inline-flex items-center gap-1">
              <ArrowDown className="h-3 w-3" />{inCount} received
            </span>
          </div>
        </DialogHeader>

        {/* Thread */}
        <div
          ref={scrollRef}
          className="max-h-[45vh] overflow-y-auto p-4 space-y-2 bg-zinc-50"
          data-testid="conversation-thread"
        >
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-zinc-500">
              {loading ? "Loading…" : "No prior messages with this number."}
            </div>
          ) : items.map((m, i) => {
            const isOut = m.direction === "out";
            const style = INTENT_STYLE[m.intent] || null;
            const failed = isOut && (m.status || "").toUpperCase() !== "SUCCESS";
            return (
              <div
                key={i}
                className={"flex " + (isOut ? "justify-end" : "justify-start")}
                data-testid={`conv-item-${m.direction}-${i}`}
              >
                <div className={
                  "max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm " +
                  (isOut
                    ? "bg-zinc-900 text-white rounded-br-sm"
                    : "bg-white border border-zinc-200 text-zinc-900 rounded-bl-sm")
                }>
                  <div className="whitespace-pre-wrap">{m.body}</div>
                  <div className={
                    "mt-1 flex flex-wrap items-center gap-2 text-2xs " +
                    (isOut ? "text-zinc-400" : "text-zinc-500")
                  }>
                    <span className="font-mono-num">{fmtDateTime(m.ts)}</span>
                    {isOut && failed && (
                      <span className="inline-flex items-center gap-1 text-red-400">
                        <AlertTriangle className="h-3 w-3" />
                        {m.status}{m.error_text ? ` · ${m.error_text}` : ""}
                      </span>
                    )}
                    {isOut && !failed && (
                      <span className="inline-flex items-center gap-1 text-emerald-400">
                        <CheckCircle2 className="h-3 w-3" />{m.source || "sent"}
                      </span>
                    )}
                    {!isOut && style && (
                      <Badge variant="outline" className={"text-2xs py-0 px-1.5 " + style.cls}>
                        {style.label}
                      </Badge>
                    )}
                    {!isOut && m.action && (
                      <span title={m.action} className="italic truncate max-w-[200px]">
                        ↳ {m.action}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <div className="p-4 border-t border-zinc-200 space-y-2">
          {templates.length > 0 && (
            <div className="flex items-center gap-2">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500 whitespace-nowrap">Template</Label>
              <Select value={selectedTpl} onValueChange={applyTemplate}>
                <SelectTrigger className="border-zinc-300 h-8 text-xs" data-testid="conv-template-select">
                  <SelectValue placeholder="Load template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder={recipientName ? `Reply to ${firstName || recipientName}…` : "Type your reply…"}
            className="border-zinc-300 font-mono-num text-sm"
            data-testid="conv-body"
          />
          <div className="flex items-center gap-2">
            <div className="flex flex-wrap gap-1 text-2xs">
              <span className="text-zinc-500">Merge:</span>
              {MERGE_HINTS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setBody((b) => b + h)}
                  className="font-mono-num text-zinc-700 hover:text-zinc-950 hover:underline"
                >
                  {h}
                </button>
              ))}
            </div>
            <span className="ml-auto font-mono-num text-2xs text-zinc-500">
              {body.length} chars · ~{Math.max(1, Math.ceil(body.length / 160))} part(s)
            </span>
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              size="sm"
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="conv-send-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
